/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import AgencyAdminTrackMetadataEntity from 'Models/Security/Acl/AgencyAdminTrackMetadataEntity';
import AgencyPlaylisterTrackMetadataEntity from 'Models/Security/Acl/AgencyPlaylisterTrackMetadataEntity';
import MustardAdminTrackMetadataEntity from 'Models/Security/Acl/MustardAdminTrackMetadataEntity';
import MustardManagementTrackMetadataEntity from 'Models/Security/Acl/MustardManagementTrackMetadataEntity';
import ApplicationUserTrackMetadataEntity from 'Models/Security/Acl/ApplicationUserTrackMetadataEntity';
import VisitorsTrackMetadataEntity from 'Models/Security/Acl/VisitorsTrackMetadataEntity';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface ITrackMetadataEntityAttributes extends IModelAttributes {
	bucket: Enums.bucket;
	energy: Enums.energy;

	trackId: string;
	track: Models.TrackEntity | Models.ITrackEntityAttributes;
	genreId: string;
	genre: Models.GenreEntity | Models.IGenreEntityAttributes;
	// % protected region % [Add any custom attributes to the interface here] on begin
	trackName: string;
	genreName: string;
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('TrackMetadataEntity', 'Track Metadata')
// % protected region % [Customise your entity metadata here] end
export default class TrackMetadataEntity extends Model
	implements ITrackMetadataEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new AgencyAdminTrackMetadataEntity(),
		new AgencyPlaylisterTrackMetadataEntity(),
		new MustardAdminTrackMetadataEntity(),
		new MustardManagementTrackMetadataEntity(),
		new ApplicationUserTrackMetadataEntity(),
		new VisitorsTrackMetadataEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	// % protected region % [Modify props to the crud options here for attribute 'Bucket'] off begin
	@observable
	@attribute<TrackMetadataEntity, Enums.bucket>()
	@CRUD({
		name: 'Bucket',
		displayType: 'enum-combobox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.bucketOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.bucketOptions),
		displayFunction: (attr: Enums.bucket) => Enums.bucketOptions[attr],
	})
	public bucket: Enums.bucket;
	// % protected region % [Modify props to the crud options here for attribute 'Bucket'] end

	// % protected region % [Modify props to the crud options here for attribute 'Energy'] off begin
	@observable
	@attribute<TrackMetadataEntity, Enums.energy>()
	@CRUD({
		name: 'Energy',
		displayType: 'enum-combobox',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.energyOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.energyOptions),
		displayFunction: (attr: Enums.energy) => Enums.energyOptions[attr],
	})
	public energy: Enums.energy;
	// % protected region % [Modify props to the crud options here for attribute 'Energy'] end

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Track'] off begin
		name: 'Track',
		displayType: 'reference-combobox',
		order: 30,
		referenceTypeFunc: () => Models.TrackEntity,
		// % protected region % [Modify props to the crud options here for reference 'Track'] end
	})
	public trackId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public track: Models.TrackEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Genre'] off begin
		name: 'Genre',
		displayType: 'reference-combobox',
		order: 40,
		referenceTypeFunc: () => Models.GenreEntity,
		// % protected region % [Modify props to the crud options here for reference 'Genre'] end
	})
	public genreId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public genre: Models.GenreEntity;

	// % protected region % [Add any custom attributes to the model here] on begin
	@CRUD({
		name: 'Track',
		displayType: 'hidden',
		headerColumn: true,
		isReadonly: true,
		displayFunction: (_attr: string, model: Model) => (model as TrackMetadataEntity).trackName,
	})
	public trackName: string;

	@CRUD({
		name: 'Genre',
		displayType: 'hidden',
		headerColumn: true,
		isReadonly: true,
		displayFunction: (_attr: string, model: Model) => (model as TrackMetadataEntity).genreName,
	})
	public genreName: string;
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<ITrackMetadataEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] on begin
		this.bucket = attributes?.bucket || 'U';
		this.energy = attributes?.energy || 'ZERO';

		// This is assigned in assignAttributes but somehow
		// it doesn't show in CRUD tables calling the following
		// this.models = data[dataModelName].map((e: any) => new ModelType(e));
		// Putting this here assigns it correctly
		if (attributes?.trackName) {
			this.trackName = attributes.trackName;
		}
		if (attributes?.genreName) {
			this.genreName = attributes.genreName;
		}
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<ITrackMetadataEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.bucket !== undefined) {
				this.bucket = attributes.bucket;
			}
			if (attributes.energy !== undefined) {
				this.energy = attributes.energy;
			}
			if (attributes.trackId !== undefined) {
				this.trackId = attributes.trackId;
			}
			if (attributes.track !== undefined) {
				if (attributes.track === null) {
					this.track = attributes.track;
				} else if (attributes.track instanceof Models.TrackEntity) {
					this.track = attributes.track;
					this.trackId = attributes.track.id;
				} else {
					this.track = new Models.TrackEntity(attributes.track);
					this.trackId = this.track.id;
				}
			}
			if (attributes.genreId !== undefined) {
				this.genreId = attributes.genreId;
			}
			if (attributes.genre !== undefined) {
				if (attributes.genre === null) {
					this.genre = attributes.genre;
				} else if (attributes.genre instanceof Models.GenreEntity) {
					this.genre = attributes.genre;
					this.genreId = attributes.genre.id;
				} else {
					this.genre = new Models.GenreEntity(attributes.genre);
					this.genreId = this.genre.id;
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] on begin
			if (attributes.trackName !== undefined) {
				this.trackName = attributes.trackName;
			}
			if (attributes.genreName !== undefined) {
				this.genreName = attributes.genreName;
			}
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		track {
			${Models.TrackEntity.getAttributes().join('\n')}
			${Models.TrackEntity.getFiles().map(f => f.name).join('\n')}
		}
		genre {
			${Models.GenreEntity.getAttributes().join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return `${this.bucket} | ${Enums.energyOptions[this.energy]}${this.genre ? `| ${this.genre?.name}` : ''}`;
		// % protected region % [Customise the display name for this entity] end
	}

	// % protected region % [Add any further custom model features here] on begin
	public listExpands = `
		trackName
		genreName
	`;
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(TrackMetadataEntity.prototype, 'created');
CRUD(modifiedAttr)(TrackMetadataEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
